import { generateTestId } from "@client-shared";
import {
  ADMIN_BADGE_VALUES,
  ADMIN_CHARACTER_VALUES,
  type AdminAttributeChange,
  type AdminAttributeField,
  isAdminBadge,
  isAdminCharacter,
} from "@shared/types/admin-product-update";
import { FLAVOUR_ZONES, INTENSITY_POSITIONS, type IntensityZone, isIntensityPosition, isIntensityZone } from "@shared/types/intensity";
import type { AdminCatalogueRow } from "./admin-catalogue-rows";

/** The shared `Button` sets no cursor and Tailwind's preflight doesn't either; disabled controls must not look clickable. */
export const EDIT_CONTROL_CURSOR = "cursor-pointer disabled:cursor-not-allowed";

/**
 * Marks a control an editor renders in a portal (a Select's options, the date picker) as that editor's own, by the
 * editor's id: portalled, it sits outside the editor's popover, so a click in it looks like a click outside.
 */
export const EDITOR_OWNER_ATTRIBUTE = "data-editor-owner";

/**
 * Whether a click outside an editor popover should close it: only when nothing in it has been changed, so stray clicks
 * never discard a manager's input, and never for a click in that editor's own portalled controls. Another editor's
 * dropdowns or popover don't count as its own, so clicking into them still closes an untouched editor.
 */
export const shouldCloseOnOutsideInteraction = (target: EventTarget | null, hasChanges: boolean, editorId: string): boolean => {
  if (hasChanges) return false;
  const owner = target instanceof Element ? target.closest(`[${EDITOR_OWNER_ATTRIBUTE}]`)?.getAttribute(EDITOR_OWNER_ATTRIBUTE) : null;
  return owner !== editorId;
};

/**
 * Whether the manager has changed the picked values since the editor opened, in any order. Compared with the editor's
 * own starting selection, not the catalogue value: badges outside the picklist are left out of that selection, which
 * isn't an edit.
 */
export const setAdminAttributeEditTestId = (identifier: string): string => generateTestId("AdminAttributeEdit", identifier);

export const setAdminWebStatusEditTestId = (identifier: string): string => generateTestId("AdminWebStatusEdit", identifier);

export const setAdminUpdateToastTestId = (identifier: string): string => generateTestId("AdminUpdateToast", identifier);

/** Column header translation key for each editable attribute. */
export const ATTRIBUTE_LABEL_KEYS = {
  intensityZone: "admin.productCatalogue.columns.intensity",
  intensityPosition: "admin.productCatalogue.columns.position",
  character: "admin.productCatalogue.columns.character",
  badges: "admin.productCatalogue.columns.badges",
} as const satisfies Record<AdminAttributeField, string>;

const INTENSITY_ZONES = Object.keys(FLAVOUR_ZONES) as IntensityZone[];

/** Values a manager can pick for each attribute. Badges are a multi-select; the rest pick one. */
export const ATTRIBUTE_OPTIONS: Record<AdminAttributeField, readonly string[]> = {
  intensityZone: INTENSITY_ZONES,
  intensityPosition: INTENSITY_POSITIONS.map(String),
  character: ADMIN_CHARACTER_VALUES,
  badges: ADMIN_BADGE_VALUES,
};

/**
 * The editor's selection for every field, as strings: single-value fields hold at most one. Badges outside the
 * picklist can't be submitted, so they start unselected and the confirmation shows them being removed.
 */
export const toAttributeDraft = (row: AdminCatalogueRow, field: AdminAttributeField): string[] => {
  switch (field) {
    case "intensityZone":
      return row.intensityZone ? [row.intensityZone] : [];
    case "intensityPosition":
      return row.intensityPosition === undefined ? [] : [String(row.intensityPosition)];
    case "character":
      return row.character && isAdminCharacter(row.character) ? [row.character] : [];
    case "badges":
      return row.badges.filter(isAdminBadge);
  }
};

const sameSet = (a: readonly string[], b: readonly string[]): boolean => a.length === b.length && a.every((value) => b.includes(value));

/** Whether the editor's selection differs from what it opened with, whatever the order. */
export const isDraftChanged = (initial: readonly string[], draft: readonly string[]): boolean => !sameSet(initial, draft);

/** The change to submit, or undefined while nothing valid and different from the current value is selected. */
export const buildAttributeChange = (
  row: AdminCatalogueRow,
  field: AdminAttributeField,
  draft: readonly string[]
): AdminAttributeChange | undefined => {
  const [first] = draft;
  switch (field) {
    case "intensityZone":
      return isIntensityZone(first) && first !== row.intensityZone
        ? { field, oldValue: row.intensityZone ?? null, newValue: first }
        : undefined;
    case "intensityPosition": {
      const position = Number(first);
      return first !== undefined && isIntensityPosition(position) && position !== row.intensityPosition
        ? { field, oldValue: row.intensityPosition ?? null, newValue: position }
        : undefined;
    }
    case "character":
      return isAdminCharacter(first) && first !== row.character ? { field, oldValue: row.character ?? null, newValue: first } : undefined;
    case "badges": {
      const badges = draft.filter(isAdminBadge);
      return sameSet(badges, row.badges) ? undefined : { field, oldValue: row.badges, newValue: badges };
    }
  }
};
